import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { PlusCircle, Trash2, Edit, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../../../ui/alert-dialog';
import { getAdditionalPayments } from '../../../../provider/adm/profesores/BankData/AdditionalPayments/getAdditionalPayments'; // Adjust the import path as necessary
import { crearAdditionalPayments } from '../../../../provider/adm/profesores/BankData/AdditionalPayments/crearAdditionalPayments';
import { updateAdditionalPayments } from '../../../../provider/adm/profesores/BankData/AdditionalPayments/updateAdditionalPayments'; // Adjust the import path as necessary
const AdditionalPaymentsDialogAdmin = ({ isOpen, onClose, teacherId, teacherName, date }) => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [formData, setFormData] = useState({
        description: '',
        amount: '',
        date: new Date().toISOString().slice(0, 16)
    });

    // Fetch additional payments for admin
    const fetchPayments = async () => {
        if (!teacherId) return;

        try {
            setLoading(true);
            const data = await getAdditionalPayments(teacherId, date.year, date.month)
            if (data && data.length > 0) {
                setPayments(data);
            } else {
                setPayments([]);
            }
         
        } catch (error) {
            console.error('Error fetching payments:', error);
            toast.error('Error fetching additional payments');
        } finally {
            setLoading(false);
        }
    };

    // Create new payment
    const createPayment = async (e) => {
        e.preventDefault();

        if (!formData.description || !formData.amount) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);
            const paymentData = {
                teacherId: parseInt(teacherId),
                date: new Date(formData.date).toISOString(),
                description: formData.description,
                amount: parseNumber(formData.amount)
            };
            await crearAdditionalPayments(paymentData);

            toast.success('Additional payment created successfully');


        } catch (error) {
            console.error('Error creating payment:', error);
            toast.error('Error creating additional payment');
        } finally {
            setFormData({
                description: '',
                amount: '',
                date: new Date().toISOString().slice(0, 16)
            });
            setShowCreateForm(false);
            fetchPayments();
            setLoading(false);
        }
    };

    // Update payment
    const updatePayment = async (paymentId, updatedData) => {
        try {
            setLoading(true);
            await updateAdditionalPayments(updatedData, paymentId);
            toast.success('Payment updated successfully');

        } catch (error) {
            console.error('Error updating payment:', error);
            toast.error('Error updating payment');
        } finally {
            setEditingPayment(null);
            fetchPayments();
            setLoading(false);
        }
    };

    const deletePayment = async (paymentId) => {
        try {
            setLoading(true);
            const token = sessionStorage.getItem('token');
            await fetch(`${process.env.REACT_APP_API_URL}/admin/profesor/pago-adicional/eliminar/${paymentId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            toast.success('Payment deleted successfully');

        } catch (error) {
            console.error('Error deleting payment:', error);
            toast.error('Error deleting payment');
        } finally {
            fetchPayments();
            setLoading(false);
        }
    };

    // Handle edit mode
    const startEditing = (payment) => {
        setEditingPayment({
            ...payment,
            amount: formatNumber(payment.amount),
            date: new Date(payment.date).toISOString().slice(0, 16)
        });
    };

    const saveEdit = () => {
        updatePayment(editingPayment.id, {
            description: editingPayment.description,
            amount: parseNumber(editingPayment.amount),
            date: new Date(editingPayment.date).toISOString(),
            teacherId: parseInt(teacherId)
        });
    };

    const cancelEdit = () => {
        setEditingPayment(null);
    };

    // Calculate total
    const totalAmount = payments.reduce((sum, payment) => sum + payment.amount, 0);

    useEffect(() => {
        if (isOpen && teacherId) {
            fetchPayments();
        }
    }, [isOpen, teacherId, date]);
    const formatNumber = (value) => {
        if (!value) return "";
        // Remove non-digit characters
        const number = value.toString().replace(/\D/g, "");
        // Format with thousands separator
        return new Intl.NumberFormat('es-CO').format(number);
    };
    const parseNumber = (formattedValue) => {
        if (!formattedValue) return 0;
        return parseInt(formattedValue.replace(/\D/g, ""), 10);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        Additional Payments - {teacherName} (ID: {teacherId})
                    </DialogTitle>
                    <p className="text-sm text-gray-600">
                        Period: {date.month}/{date.year}
                    </p>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Create Payment Form */}
                    {showCreateForm && (
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <h3 className="text-lg font-semibold mb-4">Create New Additional Payment</h3>
                            <form onSubmit={createPayment} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Description *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Payment description..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount (COP) *
                                        </label>
                                        <input
                                            type="character"
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: formatNumber(e.target.value) })}
                                            placeholder="0.00"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Date *
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Button type="submit" disabled={loading}>
                                        {loading ? 'Creating...' : 'Create Payment'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowCreateForm(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold">Payment History</h3>
                        {!showCreateForm && (
                            <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
                                <PlusCircle className="w-4 h-4" />
                                Add Payment
                            </Button>
                        )}
                    </div>

                    {/* Payments Table */}
                    {loading ? (
                        <div className="text-center py-8">Loading...</div>
                    ) : (
                        <div className="border rounded-lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>ID</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                        <TableHead className="text-center">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {payments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                                No additional payments found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        payments.map((payment) => (
                                            <TableRow key={payment.id}>
                                                <TableCell>{payment.id}</TableCell>
                                                <TableCell>
                                                    {editingPayment?.id === payment.id ? (
                                                        <input
                                                            type="datetime-local"
                                                            value={editingPayment.date}
                                                            onChange={(e) => setEditingPayment({
                                                                ...editingPayment,
                                                                date: e.target.value
                                                            })}
                                                            className="w-full px-2 py-1 border rounded text-xs"
                                                        />
                                                    ) : (
                                                        new Date(payment.date).toLocaleString("en", {
                                                            year: "numeric",
                                                            month: "short",
                                                            day: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {editingPayment?.id === payment.id ? (
                                                        <input
                                                            type="text"
                                                            value={editingPayment.description}
                                                            onChange={(e) => setEditingPayment({
                                                                ...editingPayment,
                                                                description: e.target.value
                                                            })}
                                                            className="w-full px-2 py-1 border rounded"
                                                        />
                                                    ) : (
                                                        payment.description
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {editingPayment?.id === payment.id ? (
                                                        <input
                                                            type="character"
                                                            value={formatNumber(editingPayment.amount)}
                                                            onChange={(e) => setEditingPayment({
                                                                ...editingPayment,
                                                                amount: formatNumber(e.target.value)
                                                            })}
                                                            className="w-full px-2 py-1 border rounded text-right"
                                                        />
                                                    ) : (
                                                        new Intl.NumberFormat("es-CO", {
                                                            style: "currency",
                                                            currency: "COP",
                                                        }).format(payment.amount)
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex justify-center gap-2">
                                                        {editingPayment?.id === payment.id ? (
                                                            <>
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={saveEdit}
                                                                    disabled={loading}
                                                                >
                                                                    <Save className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={cancelEdit}
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={() => startEditing(payment)}
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                </Button>
                                                                <AlertDialog>
                                                                    <AlertDialogTrigger asChild>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            className="text-red-600 hover:text-red-800"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </Button>
                                                                    </AlertDialogTrigger>
                                                                    <AlertDialogContent>
                                                                        <AlertDialogHeader>
                                                                            <AlertDialogTitle>Delete Payment</AlertDialogTitle>
                                                                            <AlertDialogDescription>
                                                                                Are you sure you want to delete this additional payment?
                                                                                This action cannot be undone.
                                                                            </AlertDialogDescription>
                                                                        </AlertDialogHeader>
                                                                        <AlertDialogFooter>
                                                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                            <AlertDialogAction
                                                                                onClick={() => deletePayment(payment.id)}
                                                                                className="bg-red-600 hover:bg-red-700"
                                                                            >
                                                                                Delete
                                                                            </AlertDialogAction>
                                                                        </AlertDialogFooter>
                                                                    </AlertDialogContent>
                                                                </AlertDialog>
                                                            </>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                    {payments.length > 0 && (
                                        <TableRow className="bg-gray-50 font-semibold">
                                            <TableCell colSpan={3} className="text-right">Total:</TableCell>
                                            <TableCell className="text-right">
                                                {new Intl.NumberFormat("es-CO", {
                                                    style: "currency",
                                                    currency: "COP",
                                                }).format(totalAmount)}
                                            </TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AdditionalPaymentsDialogAdmin;